import React, { useState, useCallback, forwardRef, lazy, Suspense } from "react";
import { graphql, useStaticQuery, Slice } from "gatsby";
import PropTypes from "prop-types";
import { Link } from "./link";
import User from "./user";
import Cart from "./cart";
import PreviewBar from "./preview-bar";
import useClickOut from "../hooks/use-click-out";
import useBlockScroll from "../hooks/use-block-scroll";
import Logo from "../icons/logo.svg";
import SearchIcon from "../icons/cerca.svg";
import MainBenefits from "./main-benefits";
import classNames from "classnames";
import BannerTopMenu from "./banner-top-menu";
import BannerPushNotification from "../components/elements/banner-push-notification";
import UtilityLink from "./utility-link";
import { hasEcommerce } from "../utility/ecommerce";

const LazySearch = lazy(() => import("./search"));

const NO_SALESFORCE = process.env.GATSBY_NO_SALESFORCE;
const externalPDPShopify = process.env.GATSBY_SHOPIFY_EXTERNAL_PDP || false;

const Header = forwardRef(
  (
    {
      siteTitle,
      hasScrolled,
      toggleHamburger,
      mobileMenuVisible,
      hamburgerRef,
      bannerTop,
      bannerRef,
      hideMainBenefits,
      mainBenefits,
      searchVisible,
      setSearchVisible,
      bannerTopMenuHeight,
      headerHeight,
    },
    ref
  ) => {
    const [search, setSearch] = useState(searchVisible);
    const toggleSearch = useCallback(() => {
      setSearch((value) => !value);
      setSearchVisible((value) => !value);
    }, [setSearchVisible]);
    const searchRef = useClickOut(search, setSearch);
    useBlockScroll(search);

    const { labelManagerLabels, config } = useStaticQuery(graphql`
      {
        labelManagerLabels: labelManagerLabels {
          search
          menu
        }
        config: liferayGeneralConfiguration {
          shopLink
        }
      }
    `);

    return (
      <div ref={ref} className="header">
        {process.env.EDIT_MODE === "true" && <PreviewBar />}
        {bannerTop && (
          <BannerPushNotification
            sleekNoteId="sn-785b13d4-ee9e-4fc6-acfe-e7bcc50e61dd"
            topMenu={true}
          >
            <BannerTopMenu ref={bannerRef} data={bannerTop} />
          </BannerPushNotification>
        )}
        <div className="header__top">
          <div className="container-fluid wrapper">
            <div className="row">
              <div className="col-4">
                <div className="d-lg-none">
                  <button
                    className={`hamburger${mobileMenuVisible ? " open" : ""}`}
                    onClick={toggleHamburger}
                    aria-label={labelManagerLabels.menu}
                  >
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </button>
                </div>
                <UtilityLink className="d-none d-lg-block" />
              </div>
              <div className="col-4">
                <div className="logo text-center">
                  <Link className="logo__link" to="/" aria-label={siteTitle}>
                    <Logo />
                  </Link>
                </div>
              </div>
              <div className="col-4 position-static">
                <div className="d-flex justify-content-end">
                  {(hasEcommerce || config?.shopLink) && !externalPDPShopify && (
                    <Cart shopLink={config.shopLink} />
                  )}
                  {!NO_SALESFORCE && <User />}
                  <div className="utility-link">
                    <button
                      className={`utility-link__btn utility-link__btn--icon${
                        search ? " open" : ""
                      }`}
                      onClick={toggleSearch}
                      aria-label={labelManagerLabels.search}
                    >
                      <SearchIcon />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          ref={hamburgerRef}
          className={classNames("header__bottom", mobileMenuVisible ? " open" : "")}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <Slice
                  alias="site-main-menu"
                  hasScrolled={hasScrolled}
                  mobileMenuVisible={mobileMenuVisible}
                  bannerTopMenuHeight={bannerTopMenuHeight}
                  headerHeight={headerHeight}
                  bannerTop={bannerTop}
                />
              </div>
            </div>
          </div>
        </div>
        {hideMainBenefits !== "yes" && <MainBenefits data={mainBenefits} />}
        {search && (
          <Suspense>
            <LazySearch ref={searchRef} open={search} toggleSearch={toggleSearch} />
          </Suspense>
        )}
      </div>
    );
  }
);

Header.propTypes = {
  siteTitle: PropTypes.string,
};

Header.defaultProps = {
  siteTitle: ``,
};

export default Header;
